import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Stack,
  Divider,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';

// Icons Import
import {
  AttachEmailOutlined,
  CardMembershipOutlined,
  KeyboardArrowRight,
  LockOpenOutlined,
  LockOutlined,
  MoreHoriz,
  PieChartOutline,
  CalendarTodayOutlined,
  FilterAltOutlined,
  OpenInNew,
} from '@mui/icons-material';

// Helpers & Components Import
import { getReportsV2, getStatePersonalizedV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import { getYears } from '../../../../helpers/dateHelpers';
import { reportStatuses, reportTypes, userCats } from '../../../../helpers/appstate';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import AppPagination from '../../../common/AppPagination';
import GenerateCertificate from '../../facilities2/facility-quarters/GenerateCertificate';
import SendCertificate from '../../facilities2/facility-quarters/SendCertificate';
import ResultLock from '../../result/result-lock/ResultLock';

export default function FacilityResults({ props, facilityId }) {
  const { userCategory, userPermission, userId, history } = props;
  const userPerm = userPermission?.map((mp) => mp.trim().toLowerCase()) || [];
  const canLockUnlockReport = userPerm.includes('lock_report');

  const years = getYears();
  const currentYear = new Date().getFullYear()?.toString();
  const pageSize = 10;

  const reportquarters = [
    { value: 1, label: 'Q1' },
    { value: 2, label: 'Q2' },
    { value: 3, label: 'Q3' },
    { value: 4, label: 'Q4' },
  ];

  const planningStatuses = {
    DRAFT: 'DRAFT',
    ONGOING: 'ONGOING',
    PUBLISHED: 'PUBLISHED',
    APPROVED: 'APPROVED',
    SUBMITTED: 'SUBMITTED',
    REJECTED: 'REJECTED',
    PLANNED: 'PLANNED',
    NOTPLANNED: 'NOT PLANNED',
  };

  const initState = { stateId: '', stateName: '' };

  // --- CORE SYSTEM STATES ---
  const [resultList, setResultList] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [resultLoading, setResultLoading] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [anchorFilterDropdownEl, setAnchorFilterDropdownMenuEl] = useState(null);
  const [anchorOverflowMenuEl, setAnchorOverflowMenuEl] = useState(null);
  const [resultSelected, setResultSelected] = useState(null);

  // --- CONTROLLABLE DIALOG STATES ---
  const [openResultLocker, setOpenResultLocker] = useState(false);
  const [openGenCert, setOpenGenCert] = useState(false);
  const [openSendCert, setOpenSendCert] = useState(false);

  // --- APPLIED FILTERS STATE ---
  const [reportYear, setReportYear] = useState(currentYear);
  const [reportType, setReportType] = useState('qaqc');
  const [reportQuarter, setReportQuarter] = useState('1');
  const [reportStatus, setReportStatus] = useState('');
  const [plannedStatus, setPlannedStatus] = useState('');
  const [stateId, setStateId] = useState('');

  // --- DROPDOWN TEMPORARY FILTERS STATE ---
  const [tempReportStatus, setTempReportStatus] = useState('');
  const [tempPlannedStatus, setTempPlannedStatus] = useState('');
  const [tempStateSelected, setTempStateSelected] = useState(initState);

  // --- FILTER METADATA STATE ---
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);

  // Fetch initial metadata configuration
  useEffect(() => {
    fetchStateList();
  }, []);

  // Main list polling effect driven exclusively by committed applied filter states
  useEffect(() => {
    fetchResultList();
  }, [
    reportYear,
    reportType,
    reportQuarter,
    reportStatus,
    plannedStatus,
    stateId,
    facilityId,
    pageNum,
  ]);

  useEffect(() => {
    if (refreshPage) fetchResultList();
  }, [refreshPage]);

  // Dropdown visibility triggers
  const handleFilterDropdownClick = (event) => {
    setTempReportStatus(reportStatus);
    setTempPlannedStatus(plannedStatus);
    setTempStateSelected(stateList.find((s) => s.stateId === stateId) || initState);
    setAnchorFilterDropdownMenuEl(event.currentTarget);
  };
  const handleFilterDropdownClose = () => setAnchorFilterDropdownMenuEl(null);

  const handleOverflowMenuClick = (event) => setAnchorOverflowMenuEl(event.currentTarget);
  const handleOverflowMenuClose = () => setAnchorOverflowMenuEl(null);

  // --- FILTER MANIPULATION LOGIC ---
  const handleSelectResultYear = (selectedYear) => {
    setReportYear(selectedYear);
    setPageNum(1);
  };

  const handleSelectResultType = (selectedType) => {
    setReportType(selectedType.toLowerCase());
    setPageNum(1);
  };

  const handleSelectQuarter = (selectedQuarter) => {
    setReportQuarter(
      reportQuarter === selectedQuarter.toString() ? '' : selectedQuarter.toString()
    );
    setPageNum(1);
  };

  const handleApplyFilters = () => {
    setReportStatus(tempReportStatus);
    setPlannedStatus(tempPlannedStatus);
    setStateId(tempStateSelected?.stateId || '');
    setPageNum(1);
    handleFilterDropdownClose();
  };

  // --- CHIP CANCEL HANDLERS ---
  const handleClearReportStatus = () => {
    setReportStatus('');
    setPageNum(1);
  };
  const handleClearPlannedStatus = () => {
    setPlannedStatus('');
    setPageNum(1);
  };
  const handleClearState = () => {
    setStateId('');
    setPageNum(1);
  };

  const fetchResultList = async () => {
    setResultLoading(true);
    try {
      const res = await getReportsV2(
        '',
        facilityId || '',
        stateId,
        reportStatus,
        reportYear,
        reportType,
        plannedStatus,
        reportQuarter,
        pageNum,
        pageSize
      );
      const { data, status, totalCount } = res?.result;
      if (status?.code === '00') {
        setResultList(data || []);
        setTotal(totalCount || 0);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Error fetching result records' });
    } finally {
      setResultLoading(false);
      setRefreshPage(false);
    }
  };

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      if (res?.result?.status?.code === '00') setStateList(res?.result?.data || []);
    } catch (error) {
      // Only feeds the State filter's options; some users (e.g. personnel) can't load the
      // state list, so fail quietly and leave the filter empty rather than showing an error
      setStateList([]);
    } finally {
      setStateLoading(false);
    }
  };

  const renderTable = () => {
    const columns = [
      // {
      //   name: 'facility',
      //   label: 'Facility Details',
      //   options: {
      //     customHeadLabelRender: (a) => (
      //       <Typography
      //         variant="button"
      //         fontWeight={700}
      //         color="text.secondary"
      //         sx={{ fontSize: '0.75rem' }}
      //       >
      //         {a.label}
      //       </Typography>
      //     ),
      //     customBodyRenderLite: (index) => (
      //       <Box>
      //         <Link
      //           to={`/facilities/${resultList[index].facilityId}/view`}
      //           style={{ textDecoration: 'none' }}
      //         >
      //           <Typography
      //             variant="body2"
      //             fontWeight={700}
      //             color="primary"
      //             sx={{ '&:hover': { textDecoration: 'underline' } }}
      //           >
      //             {resultList[index].facility}
      //           </Typography>
      //         </Link>
      //         <Typography
      //           variant="caption"
      //           color="textSecondary"
      //           sx={{ textTransform: 'capitalize', display: 'block' }}
      //         >
      //           {resultList[index].stateName?.toLowerCase()} • {resultList[index].sectorName}
      //         </Typography>
      //       </Box>
      //     ),
      //   },
      // },
      {
        name: 'reportTitle',
        label: 'Monitoring Report',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const item = resultList[index];
            const progress = Math.round(Number(item.resultProgressPercentage || 0));

            let progressColor = '#d32f2f';
            if (progress >= 35 && progress < 75) progressColor = '#ed6c02';
            if (progress >= 75) progressColor = '#2e7d32';

            return (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1, minWidth: 100 }}>
                <Box sx={{ width: '100%' }}>
                  <Typography
                    variant="body2"
                    fontWeight={item.reportTitle === 'No Report Created' ? 400 : 700}
                    color={
                      item.reportTitle === 'No Report Created' ? 'text.disabled' : 'text.primary'
                    }
                  >
                    {item.reportTitle}
                  </Typography>

                  {item.reportId ? (
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(progressColor, 0.15),
                          '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 3 },
                        }}
                      />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Entries: {item.entriesCount}/{item.totalSamplePoints}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: progressColor, fontWeight: 700 }}
                        >
                          {progress}%
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box sx={{ height: 21 }} />
                  )}
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'plannedReportStatus',
        label: 'Schedule Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const planStatus = resultList[index].plannedReportStatus;
            let chipColor = '#757575';
            const statusLower = planStatus?.toLowerCase();

            if (statusLower === 'ongoing') chipColor = '#0288d1';
            if (statusLower === 'planned') chipColor = '#ed6c02';
            if (statusLower === 'not planned') chipColor = '#757575';
            if (statusLower === 'submitted') chipColor = '#b100cd';
            if (statusLower === 'published') chipColor = '#2e7d32';
            if (statusLower === 'approved') chipColor = '#2e7d32';
            if (statusLower === 'rejected') chipColor = '#d32f2f';

            return (
              <Chip
                label={planStatus || 'N/A'}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(chipColor, 0.1),
                  color: chipColor,
                  borderRadius: '6px',
                  textTransform: 'capitalize',
                }}
              />
            );
          },
        },
      },
      {
        name: 'reportType',
        label: 'Type',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Typography variant="body2">{resultList[index].reportType}</Typography>
          ),
        },
      },
    ];

    if (userCategory === userCats.EDM) {
      columns.push(
        {
          name: '',
          label: 'Certificate',
          options: {
            customHeadLabelRender: (a) => (
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
              >
                {a.label}
              </Typography>
            ),
            customBodyRenderLite: (index) => {
              const rowData = resultList[index];
              const hasCert = rowData.certString && rowData.certString.trim() !== '';
              const isSent = rowData.certSent;

              if (hasCert) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.1,
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip title="View Certificate in new tab">
                      <Chip
                        label="Preview Cert"
                        size="small"
                        clickable
                        component="a"
                        href={rowData.certString}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<OpenInNew sx={{ fontSize: '16px !important', ml: 1 }} />}
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha('#1976d2', 0.1),
                          color: '#1976d2',
                          '&:hover': { bgcolor: alpha('#1976d2', 0.2) },
                        }}
                      />
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isSent ? 'success.main' : 'warning.main',
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {isSent ? 'Sent' : 'Not sent'}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                  Not Generated
                </Typography>
              );
            },
          },
        },
        {
          name: '',
          label: 'Actions',
          options: {
            customHeadLabelRender: (a) => (
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                align="center"
              >
                {a.label}
              </Typography>
            ),
            customBodyRenderLite: (index) => {
              const i = resultList[index];
              return (
                <Box align="center">
                  {i.reportId && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        handleOverflowMenuClick(e);
                        setResultSelected(i);
                      }}
                    >
                      <MoreHoriz />
                    </IconButton>
                  )}
                </Box>
              );
            },
          },
        }
      );
    } else {
      columns.push({
        name: '',
        label: 'Action',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const i = resultList[index];
            return i?.isVisible ? (
              <Button
                variant="contained"
                size="small"
                disableElevation
                endIcon={<KeyboardArrowRight />}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 2 }}
                onClick={() =>
                  history.push(
                    `/results/view/${i?.facilityId}/${i?.reportId}/${i?.reportQuarter}/${i?.reportYear}`
                  )
                }
              >
                View Result
              </Button>
            ) : (
              <Typography
                variant="caption"
                color={i.reportId ? 'info.light' : 'text.disabled'}
                fontWeight={600}
              >
                {i.reportId ? 'Pending Release' : 'No Action Required'}
              </Typography>
            );
          },
        },
      });
    }

    return (
      <MUIDataTable
        data={resultList}
        columns={columns}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
          Result
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View results for this facility
        </Typography>
      </Box>

      {/* FILTERS MANAGEMENT BAR */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              value={reportYear}
              size="small"
              onChange={(e) => handleSelectResultYear(e.target.value)}
              startAdornment={
                <CalendarTodayOutlined sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              }
              sx={{ borderRadius: '10px', minWidth: 100, fontWeight: 600 }}
            >
              {years?.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>

            <ButtonGroup
              disableElevation
              variant="outlined"
              sx={{ borderRadius: '10px', overflow: 'hidden', height: 40 }}
            >
              {[reportTypes.QAQC, reportTypes.NORMAL].map((t) => (
                <Button
                  key={t}
                  onClick={() => handleSelectResultType(t.toLowerCase())}
                  sx={{
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: reportType === t.toLowerCase() ? 'primary.main' : 'transparent',
                    color: reportType === t.toLowerCase() ? 'white' : 'primary.main',
                    '&:hover': {
                      bgcolor:
                        reportType === t.toLowerCase() ? 'primary.dark' : alpha('#1976d2', 0.05),
                    },
                  }}
                >
                  {t}
                </Button>
              ))}
            </ButtonGroup>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              {reportquarters.map((q) => {
                const isSelected = reportQuarter === q.value.toString();
                return (
                  <Chip
                    key={q.value}
                    label={q.label}
                    size="small"
                    clickable
                    onClick={() => handleSelectQuarter(q.value)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{
                      fontWeight: 600,
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      height: '26px',
                    }}
                  />
                );
              })}
              <Divider sx={{ height: 24, mx: 1 }} orientation="vertical" />
            </Stack>

            <Tooltip title="Advanced Filters">
              <IconButton
                size="small"
                onClick={handleFilterDropdownClick}
                color={reportStatus || plannedStatus || stateId ? 'primary' : 'default'}
              >
                <FilterAltOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* ACTIVE RUNNING COMMITTED CHIPS */}
        {(reportStatus || plannedStatus || stateId) && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            {reportStatus && (
              <Chip
                label={`Status: ${reportStatus}`}
                onDelete={handleClearReportStatus}
                sx={{
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
            {plannedStatus && (
              <Chip
                label={`Schedule: ${plannedStatus}`}
                onDelete={handleClearPlannedStatus}
                sx={{
                  bgcolor: alpha('#4a148c', 0.1),
                  color: '#4a148c',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
            {stateId && (
              <Chip
                label={`State: ${stateList.find((s) => s.stateId === stateId)?.stateName ||
                  'Selected'}`}
                onDelete={handleClearState}
                sx={{
                  bgcolor: alpha('#ed6c02', 0.1),
                  color: '#ed6c02',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* MAIN DATA RENDERING GRID */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {resultLoading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
            <Divider sx={{ my: 2 }} />
            <LoaderSkeleton />
          </Box>
        ) : resultList?.length < 1 ? (
          <Box sx={{ py: 8 }}>
            <Empty description="No results found matching selected criteria" />
          </Box>
        ) : (
          renderTable()
        )}
      </Paper>

      {/* COMPONENT FOOTER PAGINATION */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
          Showing {resultList.length} of {total} records
        </Typography>
        <AppPagination
          defaultCurrent={1}
          defaultPageSize={pageSize}
          total={total}
          current={pageNum}
          onPageChange={(p) => setPageNum(p)}
        />
      </Box>

      {/* --- REUSABLE ADVANCED OVERLAY FILTER POPUP MENU --- */}
      <Menu
        anchorEl={anchorFilterDropdownEl}
        open={Boolean(anchorFilterDropdownEl)}
        onClose={handleFilterDropdownClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mt: 1,
            width: 280,
            p: 2.5,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: 'text.secondary',
          }}
        >
          Filter Results
        </Typography>
        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Report Status</InputLabel>
            <Select
              value={tempReportStatus}
              label="Report Status"
              onChange={(e) => setTempReportStatus(e.target.value)}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.keys(reportStatuses).map((key) => (
                <MenuItem key={key} value={reportStatuses[key]}>
                  {reportStatuses[key].toLowerCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Schedule Status</InputLabel>
            <Select
              value={tempPlannedStatus}
              label="Schedule Status"
              onChange={(e) => setTempPlannedStatus(e.target.value)}
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.keys(planningStatuses).map((key) => (
                <MenuItem key={key} value={planningStatuses[key]}>
                  {planningStatuses[key].toLowerCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            options={stateList}
            getOptionLabel={(option) => option.stateName || ''}
            value={tempStateSelected?.stateId ? tempStateSelected : null}
            onChange={(e, value) => setTempStateSelected(value || initState)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: stateLoading && <CircularProgress size={16} sx={{ mr: 1 }} />,
                }}
              />
            )}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyFilters}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Menu>

      {/* --- OVERFLOW RECORD OPTIONS CONTEXT MENU --- */}
      <Menu
        anchorEl={anchorOverflowMenuEl}
        open={Boolean(anchorOverflowMenuEl)}
        onClose={handleOverflowMenuClose}
        PaperProps={{
          sx: { borderRadius: '12px', mt: 1, width: 220, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' },
        }}
      >
        <MenuItem
          onClick={() => {
            handleOverflowMenuClose();
            history.push(
              `/results/view/${resultSelected?.facilityId}/${resultSelected?.reportId}/${resultSelected?.reportQuarter}/${resultSelected?.reportYear}`
            );
          }}
        >
          <ListItemIcon>
            <PieChartOutline fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="View Result"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </MenuItem>

        {canLockUnlockReport && (
          <MenuItem
            onClick={() => {
              setOpenResultLocker(true);
              handleOverflowMenuClose();
            }}
            disabled={resultSelected?.reportStatus !== reportStatuses.PUBLISHED}
          >
            <ListItemIcon>
              {resultSelected?.isVisible ? (
                <LockOutlined fontSize="small" />
              ) : (
                <LockOpenOutlined fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={resultSelected?.isVisible ? 'Lock Result' : 'Unlock Result'}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        <Divider />

        <MenuItem
          onClick={() => {
            setOpenGenCert(true);
            handleOverflowMenuClose();
          }}
        >
          <ListItemIcon>
            <CardMembershipOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Generate Certificate"
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setOpenSendCert(true);
            handleOverflowMenuClose();
          }}
        >
          <ListItemIcon>
            <AttachEmailOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Send Certificate" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
      </Menu>

      {/* --- CONTEXT SUBSYSTEM DIALOG OVERLAYS --- */}
      {openResultLocker && (
        <ResultLock
          open={openResultLocker}
          onClose={() => setOpenResultLocker(false)}
          record={resultSelected}
          onRefreshPage={() => setRefreshPage(true)}
        />
      )}
      {openGenCert && (
        <GenerateCertificate
          open={openGenCert}
          onClose={() => {
            setOpenGenCert(false);
            setRefreshPage(true);
          }}
          title="Generate Certificate"
          report={resultSelected}
        />
      )}
      {openSendCert && (
        <SendCertificate
          open={openSendCert}
          onClose={() => {
            setOpenSendCert(false);
            setRefreshPage(true);
          }}
          title="Send Certificate"
          report={resultSelected}
        />
      )}
    </Box>
  );
}
